import DashboardLayout from '@/components/DashboardLayout'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useState } from 'react'
import { router } from '@inertiajs/react'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Check,
  Smartphone,
  AlertTriangle,
  Info,
  FileText,
  Globe,
  LayoutGrid,
} from 'lucide-react'
import {
  PublicPageSettings,
  type SubcategoryOption,
} from './PublicPageSettings'
import { PlatformFeesDialog, type FeeRow } from '@/components/PlatformFeesInfo'
import type { CustomSchoolFeature } from '@/lib/schoolFeatures'
import type { BusinessCategory } from '@/components/public/CreateYourPage'

type HorizonUnit = 'days' | 'weeks' | 'months'

type FiscalDocumentScope = 'service_only' | 'service_and_product'
type FiscalProvider = 'conta_azul' | 'bling'

/**
 * Estado da conexão com a Conta Azul.
 *
 * Não existe mais um estado "expired": o access token vive ~1h e é renovado
 * sozinho, então tratá-lo como problema fazia a tela dizer "nenhuma venda está
 * sendo enviada" enquanto as vendas saíam normalmente.
 *
 * - `connected`         — tudo certo
 * - `provider_unstable` — a Conta Azul está fora do ar; reconectar não resolve
 * - `needs_reconnect`   — a autorização foi revogada; só o OAuth resolve
 * - `disconnected`      — nunca conectou, ou desconectou de propósito
 */
type ContaAzulStatus =
  'connected' | 'provider_unstable' | 'needs_reconnect' | 'disconnected'

interface ContaAzulInfo {
  configured: boolean
  status: ContaAzulStatus
  connected_at: string | null
  revoked_at: string | null
  revoked_reason: string | null
}

const HORIZON_LIMITS: Record<
  HorizonUnit,
  { max: number; singular: string; plural: string }
> = {
  days: { max: 365, singular: 'dia', plural: 'dias' },
  weeks: { max: 52, singular: 'semana', plural: 'semanas' },
  months: { max: 12, singular: 'mês', plural: 'meses' },
}

type DeadlineUnit = 'hours' | 'minutes'

// Prazos são guardados em minutos no banco. Na UI o admin escolhe a unidade.
// Inferimos a unidade inicial: múltiplos exatos de 60 viram horas, o resto minutos.
const inferDeadlineUnit = (minutes: number): DeadlineUnit =>
  minutes > 0 && minutes % 60 === 0 ? 'hours' : 'minutes'

const minutesToValue = (minutes: number, unit: DeadlineUnit): number =>
  unit === 'hours' ? Math.round(minutes / 60) : minutes

const valueToMinutes = (value: number, unit: DeadlineUnit): number =>
  unit === 'hours' ? value * 60 : value

const deadlineUnitLabel = (unit: DeadlineUnit, value: number): string =>
  unit === 'hours'
    ? value === 1
      ? 'hora'
      : 'horas'
    : value === 1
      ? 'minuto'
      : 'minutos'

// Exemplo para a UI: a partir de uma aula às 06:00, mostra até que horário
// o aluno consegue agendar/cancelar dado o prazo (em minutos).
const formatDeadlineExample = (totalMinutes: number): string => {
  let target = 6 * 60 - totalMinutes
  const prevDay = target < 0
  target = ((target % 1440) + 1440) % 1440
  const hh = String(Math.floor(target / 60)).padStart(2, '0')
  const mm = String(target % 60).padStart(2, '0')
  return `${hh}:${mm}${prevDay ? ' do dia anterior' : ''}`
}

interface School {
  id: number
  name: string
  primary_color: string
  secondary_color: string
  theme_mode: 'light' | 'dark'
  show_teacher_to_students: boolean
  cancellation_deadline_minutes: number
  scheduling_deadline_minutes: number
  booking_horizon_months: number
  booking_horizon_unit: HorizonUnit
  waitlist_enabled: boolean
  require_board_selection: boolean
  student_booking_disabled: boolean
  student_payment_disabled: boolean
  teacher_can_delete_open_lessons: boolean
  enrollments_enabled: boolean
  show_lesson_participants_to_students: boolean
  show_lesson_participants_to_teachers: boolean
  fiscal_enabled: boolean
  fiscal_document_scope: FiscalDocumentScope
  fiscal_provider: FiscalProvider | null
  slug: string | null
  phone: string | null
  business_category: BusinessCategory
  explore_subcategory_id: number | null
  public_page_enabled: boolean
  public_registration_enabled: boolean
  public_show_packages: boolean
  public_show_package_prices: boolean
  public_show_hours: boolean
  public_show_map: boolean
  public_show_teachers: boolean
  public_headline: string | null
  public_tagline: string | null
  public_about: string | null
  public_features: string[]
  public_show_whatsapp: boolean
  public_highlight_trial: boolean
  public_custom_features: CustomSchoolFeature[]
}

interface GalleryImage {
  id: number
  url: string
}

interface SettingsProps {
  school: School
  has_products: boolean
  has_trial_package: boolean
  conta_azul: ContaAzulInfo
  can_reset_data: boolean
  public_gallery: GalleryImage[]
  /** Catálogo do Explore, para o select de especialidade da página pública. */
  explore_subcategories: SubcategoryOption[]
  fee_table: FeeRow[]
}

const colorPresets = [
  {
    name: 'Oceano Azul',
    description: 'Clássico e profissional',
    primary: '#0ea5e9',
    secondary: '#06b6d4',
    icon: '🌊',
  },
  {
    name: 'Sunset Laranja',
    description: 'Energia do pôr do sol',
    primary: '#f97316',
    secondary: '#fb923c',
    icon: '🌅',
  },
  {
    name: 'Verde Tropical',
    description: 'Natureza vibrante',
    primary: '#10b981',
    secondary: '#14b8a6',
    icon: '🌴',
  },
  {
    name: 'Roxo Noturno',
    description: 'Moderno e elegante',
    primary: '#8b5cf6',
    secondary: '#a78bfa',
    icon: '🌙',
  },
  {
    name: 'Rosa Tropical',
    description: 'Divertido e jovem',
    primary: '#ec4899',
    secondary: '#f472b6',
    icon: '🌺',
  },
  {
    name: 'Amarelo Sol',
    description: 'Vibrante e alegre',
    primary: '#eab308',
    secondary: '#fbbf24',
    icon: '☀️',
  },
]

export default function Settings({
  school,
  has_products,
  has_trial_package,
  conta_azul,
  can_reset_data,
  public_gallery,
  explore_subcategories,
  fee_table,
}: SettingsProps) {
  // Extremos da tabela, para o resumo nao repetir percentual fixo no codigo.
  /**
   * A integração está de fato parada — nenhuma venda chega no Conta Azul até
   * alguém refazer o OAuth. `provider_unstable` fica de fora: a credencial
   * continua válida, a Conta Azul é que não respondeu, e as vendas são
   * reenviadas sozinhas.
   */
  const contaAzulHalted =
    conta_azul.status === 'needs_reconnect' ||
    conta_azul.status === 'disconnected'

  const feeRates = fee_table.map((f) => f.rate)
  const formatRate = (rate: number) => `${rate.toFixed(2).replace('.', ',')}%`
  const lowestFeeRate = feeRates.length
    ? formatRate(Math.min(...feeRates))
    : '-'
  const highestFeeRate = feeRates.length
    ? formatRate(Math.max(...feeRates))
    : '-'

  const [selectedPrimary, setSelectedPrimary] = useState(school.primary_color)
  const [selectedSecondary, setSelectedSecondary] = useState(
    school.secondary_color,
  )
  const [isLayoutSubmitting, setIsLayoutSubmitting] = useState(false)

  const [showTeacher, setShowTeacher] = useState(
    school.show_teacher_to_students,
  )
  const [schedulingUnit, setSchedulingUnit] = useState<DeadlineUnit>(
    inferDeadlineUnit(school.scheduling_deadline_minutes),
  )
  const [schedulingValue, setSchedulingValue] = useState(() =>
    minutesToValue(
      school.scheduling_deadline_minutes,
      inferDeadlineUnit(school.scheduling_deadline_minutes),
    ),
  )
  const [cancellationUnit, setCancellationUnit] = useState<DeadlineUnit>(
    inferDeadlineUnit(school.cancellation_deadline_minutes),
  )
  const [cancellationValue, setCancellationValue] = useState(() =>
    minutesToValue(
      school.cancellation_deadline_minutes,
      inferDeadlineUnit(school.cancellation_deadline_minutes),
    ),
  )
  const [bookingHorizon, setBookingHorizon] = useState(
    school.booking_horizon_months,
  )
  const [bookingHorizonUnit, setBookingHorizonUnit] = useState<HorizonUnit>(
    school.booking_horizon_unit,
  )
  const [waitlistEnabled, setWaitlistEnabled] = useState(
    school.waitlist_enabled,
  )
  const [requireBoardSelection, setRequireBoardSelection] = useState(
    school.require_board_selection,
  )
  const [studentBookingDisabled, setStudentBookingDisabled] = useState(
    school.student_booking_disabled,
  )
  const [studentPaymentDisabled, setStudentPaymentDisabled] = useState(
    school.student_payment_disabled,
  )
  const [teacherCanDeleteOpenLessons, setTeacherCanDeleteOpenLessons] =
    useState(school.teacher_can_delete_open_lessons)
  const [showParticipantsToStudents, setShowParticipantsToStudents] = useState(
    school.show_lesson_participants_to_students,
  )
  const [showParticipantsToTeachers, setShowParticipantsToTeachers] = useState(
    school.show_lesson_participants_to_teachers,
  )
  const [enrollmentsEnabled, setEnrollmentsEnabled] = useState(
    school.enrollments_enabled,
  )
  const [isModulesSubmitting, setIsModulesSubmitting] = useState(false)
  const [isAppSubmitting, setIsAppSubmitting] = useState(false)
  const [isResetting, setIsResetting] = useState(false)

  const [fiscalEnabled, setFiscalEnabled] = useState(school.fiscal_enabled)
  const [fiscalDocumentScope, setFiscalDocumentScope] =
    useState<FiscalDocumentScope>(school.fiscal_document_scope)
  const [fiscalProvider, setFiscalProvider] = useState<FiscalProvider | ''>(
    school.fiscal_provider ?? '',
  )
  const [isFiscalSubmitting, setIsFiscalSubmitting] = useState(false)
  const [isContaAzulDisconnecting, setIsContaAzulDisconnecting] =
    useState(false)

  const horizonLimit = HORIZON_LIMITS[bookingHorizonUnit]
  const horizonLabel =
    bookingHorizon === 1 ? horizonLimit.singular : horizonLimit.plural

  const handlePresetSelect = (preset: (typeof colorPresets)[0]) => {
    setSelectedPrimary(preset.primary)
    setSelectedSecondary(preset.secondary)
  }

  const isCurrentPreset = (preset: (typeof colorPresets)[0]) =>
    preset.primary === selectedPrimary && preset.secondary === selectedSecondary

  const handleLayoutSubmit = () => {
    setIsLayoutSubmitting(true)
    router.patch(
      '/settings',
      {
        school: {
          primary_color: selectedPrimary,
          secondary_color: selectedSecondary,
        },
      },
      {
        onSuccess: () => {
          toast.success('Configurações salvas! Recarregando...')
          setTimeout(() => window.location.reload(), 1000)
        },
        onError: () => {
          toast.error('Erro ao salvar configurações')
          setIsLayoutSubmitting(false)
        },
      },
    )
  }

  const handleResetData = () => {
    setIsResetting(true)
    router.post(
      '/settings/reset_data',
      {},
      {
        onSuccess: () => {
          toast.success('Dados da escola zerados com sucesso!')
          setIsResetting(false)
        },
        onError: () => {
          toast.error('Erro ao zerar os dados')
          setIsResetting(false)
        },
      },
    )
  }

  const handleModulesSubmit = () => {
    setIsModulesSubmitting(true)
    router.patch(
      '/settings',
      { school: { enrollments_enabled: enrollmentsEnabled } },
      {
        onSuccess: () => {
          toast.success('Módulos salvos!')
          setIsModulesSubmitting(false)
        },
        onError: () => {
          toast.error('Erro ao salvar os módulos.')
          setIsModulesSubmitting(false)
        },
      },
    )
  }

  const handleAppSubmit = () => {
    setIsAppSubmitting(true)
    router.patch(
      '/settings',
      {
        school: {
          show_teacher_to_students: showTeacher,
          cancellation_deadline_minutes: valueToMinutes(
            cancellationValue,
            cancellationUnit,
          ),
          scheduling_deadline_minutes: valueToMinutes(
            schedulingValue,
            schedulingUnit,
          ),
          booking_horizon_months: bookingHorizon,
          booking_horizon_unit: bookingHorizonUnit,
          waitlist_enabled: waitlistEnabled,
          require_board_selection: requireBoardSelection,
          student_booking_disabled: studentBookingDisabled,
          student_payment_disabled: studentPaymentDisabled,
          teacher_can_delete_open_lessons: teacherCanDeleteOpenLessons,
          show_lesson_participants_to_students: showParticipantsToStudents,
          show_lesson_participants_to_teachers: showParticipantsToTeachers,
        },
      },
      {
        onSuccess: () => {
          toast.success('Configurações do aplicativo salvas!')
          setIsAppSubmitting(false)
        },
        onError: () => {
          toast.error('Erro ao salvar configurações')
          setIsAppSubmitting(false)
        },
      },
    )
  }

  // "Serviço + Produto" só faz sentido se a escola tiver cadastro de produtos.
  const canUseProductScope = has_products

  const handleFiscalSubmit = () => {
    if (fiscalEnabled && !fiscalProvider) {
      toast.error('Selecione o provedor de nota fiscal')
      return
    }
    setIsFiscalSubmitting(true)
    router.patch(
      '/settings',
      {
        school: {
          fiscal_enabled: fiscalEnabled,
          fiscal_document_scope: fiscalDocumentScope,
          fiscal_provider: fiscalProvider || null,
        },
      },
      {
        onSuccess: () => {
          toast.success('Configurações fiscais salvas!')
          setIsFiscalSubmitting(false)
        },
        onError: () => {
          toast.error('Erro ao salvar configurações fiscais')
          setIsFiscalSubmitting(false)
        },
      },
    )
  }

  // Inicia o fluxo OAuth: redireciona o navegador para a tela de autorização
  // da Conta Azul (a rota faz o redirect server-side com `state` anti-CSRF).
  const handleContaAzulConnect = () => {
    globalThis.location.href = '/settings/conta_azul/connect'
  }

  const handleContaAzulDisconnect = () => {
    setIsContaAzulDisconnecting(true)
    router.delete('/settings/conta_azul', {
      onSuccess: () => toast.success('Conta Azul desconectada.'),
      onError: () => toast.error('Erro ao desconectar a Conta Azul'),
      onFinish: () => setIsContaAzulDisconnecting(false),
    })
  }

  return (
    <DashboardLayout schoolName={school.name}>
      <div className="p-6 space-y-6">
        <div>
          <h2 className="text-2xl font-bold">Configurações</h2>
          <p className="text-sm text-muted-foreground">
            Personalize sua escola e o comportamento do aplicativo
          </p>
        </div>

        <Tabs defaultValue="layout">
          <TabsList>
            <TabsTrigger value="layout">Layout</TabsTrigger>
            <TabsTrigger value="modules">
              <LayoutGrid className="h-4 w-4 mr-1.5" />
              Módulos
            </TabsTrigger>
            <TabsTrigger value="app">
              <Smartphone className="h-4 w-4 mr-1.5" />
              Aplicativo
            </TabsTrigger>
            <TabsTrigger value="fiscal">
              <FileText className="h-4 w-4 mr-1.5" />
              Nota Fiscal
            </TabsTrigger>
            <TabsTrigger value="public">
              <Globe className="h-4 w-4 mr-1.5" />
              Página Pública
            </TabsTrigger>
          </TabsList>

          {/* ── TAB LAYOUT ── */}
          <TabsContent value="layout" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Paletas de Cores</CardTitle>
                <CardDescription className="text-sm">
                  Escolha uma paleta para sua aplicação
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {colorPresets.map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => handlePresetSelect(preset)}
                      className={`p-3 rounded-lg border transition-colors text-left ${
                        isCurrentPreset(preset)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50 hover:bg-accent/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex gap-1.5">
                          <div
                            className="w-6 h-6 rounded border"
                            style={{ backgroundColor: preset.primary }}
                          />
                          <div
                            className="w-6 h-6 rounded border"
                            style={{ backgroundColor: preset.secondary }}
                          />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-sm font-medium flex items-center gap-2">
                            {preset.name}
                            {isCurrentPreset(preset) && (
                              <Check className="h-3.5 w-3.5 text-primary" />
                            )}
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            {preset.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-sm font-medium mb-4">
                    Personalização Avançada
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-xl">
                    <div className="space-y-2">
                      <Label htmlFor="primary" className="text-sm">
                        Cor Primária
                      </Label>
                      <div className="flex gap-2">
                        <input
                          id="primary"
                          type="color"
                          value={selectedPrimary}
                          onChange={(e) => setSelectedPrimary(e.target.value)}
                          className="w-16 h-10 rounded border cursor-pointer"
                        />
                        <input
                          type="text"
                          value={selectedPrimary}
                          onChange={(e) => setSelectedPrimary(e.target.value)}
                          className="flex-1 px-3 rounded border bg-background"
                          placeholder="#0ea5e9"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="secondary">Cor Secundária</Label>
                      <div className="flex gap-2">
                        <input
                          id="secondary"
                          type="color"
                          value={selectedSecondary}
                          onChange={(e) => setSelectedSecondary(e.target.value)}
                          className="w-16 h-10 rounded border cursor-pointer"
                        />
                        <input
                          type="text"
                          value={selectedSecondary}
                          onChange={(e) => setSelectedSecondary(e.target.value)}
                          className="flex-1 px-3 rounded border bg-background"
                          placeholder="#06b6d4"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Preview</CardTitle>
                <CardDescription className="text-sm">
                  Veja como ficará
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 rounded-lg border bg-accent/20">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      style={{
                        backgroundColor: selectedPrimary,
                        borderColor: selectedPrimary,
                      }}
                      className="text-white"
                    >
                      Primário
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      style={{
                        borderColor: selectedPrimary,
                        color: selectedPrimary,
                      }}
                    >
                      Outline
                    </Button>
                    <div
                      className="px-3 py-1.5 rounded text-sm text-white font-medium"
                      style={{ backgroundColor: selectedSecondary }}
                    >
                      Secundário
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => router.visit('/dashboard')}
                disabled={isLayoutSubmitting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleLayoutSubmit}
                disabled={isLayoutSubmitting}
              >
                {isLayoutSubmitting ? 'Salvando...' : 'Salvar Configurações'}
              </Button>
            </div>
          </TabsContent>

          {/* ── TAB MÓDULOS ── */}
          <TabsContent value="modules" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Módulos do painel</CardTitle>
                <CardDescription className="text-sm">
                  Nem toda escola usa todas as telas. O que estiver desligado
                  aqui deixa de aparecer no menu lateral.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Matrículas</p>
                    <p className="text-sm text-muted-foreground">
                      Ligue se a escola trabalha com matrícula do aluno. Se o
                      seu fluxo é só venda de pacote e assinatura, deixe
                      desligado — a tela some do menu, mas nada que já foi
                      matriculado se perde.
                    </p>
                  </div>
                  <Switch
                    checked={enrollmentsEnabled}
                    onCheckedChange={setEnrollmentsEnabled}
                  />
                </div>

                <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground">
                  {enrollmentsEnabled ? (
                    <p>
                      <span className="font-medium text-foreground">
                        Matrículas aparece no menu
                      </span>{' '}
                      — em Vendas, ao lado de Assinaturas.
                    </p>
                  ) : (
                    <p>
                      <span className="font-medium text-foreground">
                        Matrículas está fora do menu
                      </span>{' '}
                      — o endereço /enrollments continua abrindo para quem tiver
                      o link, então nenhum dado fica inacessível.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() =>
                  setEnrollmentsEnabled(school.enrollments_enabled)
                }
                disabled={isModulesSubmitting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleModulesSubmit}
                disabled={isModulesSubmitting}
              >
                {isModulesSubmitting ? 'Salvando...' : 'Salvar Módulos'}
              </Button>
            </div>
          </TabsContent>

          {/* ── TAB APLICATIVO ── */}
          <TabsContent value="app" className="space-y-6 mt-6">
            <Card
              className={
                !teacherCanDeleteOpenLessons
                  ? 'border-destructive/50'
                  : undefined
              }
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {!teacherCanDeleteOpenLessons && (
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  )}
                  Exclusão de Vagas pelo Professor
                </CardTitle>
                <CardDescription className="text-sm">
                  Controla se o professor pode apagar, pelo aplicativo, as
                  próprias vagas que ainda não têm aluno.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Professor pode apagar vaga sem aluno
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Vale só para horário livre. Aula que já tem aluno o
                      professor nunca apaga — para desmarcar um aluno, ele
                      cancela a aula, o que estorna o crédito e avisa o aluno.
                    </p>
                  </div>
                  <Switch
                    checked={teacherCanDeleteOpenLessons}
                    onCheckedChange={setTeacherCanDeleteOpenLessons}
                  />
                </div>

                <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground space-y-1">
                  {teacherCanDeleteOpenLessons ? (
                    <>
                      <p className="font-medium text-foreground">
                        Exclusão liberada
                      </p>
                      <p>
                        O professor apaga as próprias vagas vazias pelo app. A
                        administração é avisada a cada exclusão.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-foreground">
                        Exclusão bloqueada
                      </p>
                      <p>
                        Só a administração apaga vagas. O professor que tentar
                        pelo app vê um aviso para falar com a escola.
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card
              className={
                studentBookingDisabled ? 'border-destructive/50' : undefined
              }
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {studentBookingDisabled && (
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  )}
                  Bloquear Agendamento pelo Aluno
                </CardTitle>
                <CardDescription className="text-sm">
                  Impede que os alunos marquem aulas pelo aplicativo. Use quando
                  o agendamento deve ser feito apenas pela administração da
                  escola.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Bloquear agendamento de aulas pelo aluno
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Quando ativado, nenhum aluno consegue reservar, agendar ou
                      entrar na fila de espera pelo aplicativo. Ao tocar em
                      "Reservar", o aluno vê um aviso de que o agendamento é
                      feito somente com a administração.
                    </p>
                  </div>
                  <Switch
                    checked={studentBookingDisabled}
                    onCheckedChange={setStudentBookingDisabled}
                  />
                </div>

                <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground space-y-1">
                  {studentBookingDisabled ? (
                    <>
                      <p className="font-medium text-foreground">
                        Agendamento bloqueado
                      </p>
                      <p>
                        Os alunos não conseguem marcar aulas pelo app. Apenas a
                        administração agenda as aulas no painel.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-foreground">
                        Agendamento liberado
                      </p>
                      <p>
                        Os alunos podem marcar aulas normalmente pelo
                        aplicativo, respeitando os demais prazos e regras
                        abaixo.
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card
              className={
                studentPaymentDisabled ? 'border-destructive/50' : undefined
              }
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  {studentPaymentDisabled && (
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  )}
                  Pagamento pelo Aplicativo
                </CardTitle>
                <CardDescription className="text-sm">
                  Controle se os alunos podem comprar pacotes de aula direto
                  pelo aplicativo.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Bloquear compra de pacotes pelo aluno
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Quando ativado, o aluno não consegue comprar nenhum pacote
                      pelo aplicativo. Ao tentar pagar, ele vê um aviso para
                      falar com a escola — as vendas passam a ser registradas
                      apenas pela administração.
                    </p>
                  </div>
                  <Switch
                    checked={studentPaymentDisabled}
                    onCheckedChange={setStudentPaymentDisabled}
                  />
                </div>

                <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground space-y-1">
                  {studentPaymentDisabled ? (
                    <>
                      <p className="font-medium text-foreground">
                        Pagamento bloqueado
                      </p>
                      <p>
                        Os alunos não conseguem comprar pacotes pelo app.
                        Registre as vendas pelo painel da administração.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-foreground">
                        Pagamento liberado
                      </p>
                      <p>
                        Os alunos podem comprar pacotes normalmente pelo
                        aplicativo.
                      </p>
                    </>
                  )}
                </div>

                {/* Os numeros vem do backend (PlatformFeeTable). Antes eram fixos aqui
                    ("30 dias", "4,07%") e ficaram errados: o prazo depende da forma de
                    pagamento e a taxa varia por forma e parcelamento. */}
                <div className="rounded-md border border-amber-300/60 bg-amber-50 p-4 text-sm text-amber-900 space-y-3 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200">
                  <p className="font-medium flex items-center gap-2">
                    <Info className="h-4 w-4 shrink-0" />
                    Como funciona o pagamento pelo aplicativo
                  </p>
                  <p>
                    A taxa depende da forma de pagamento — de{' '}
                    <strong>{lowestFeeRate}</strong> no PIX a{' '}
                    <strong>{highestFeeRate}</strong> no crédito parcelado — e o
                    prazo para o dinheiro ficar disponível vai de{' '}
                    <strong>alguns segundos</strong> (PIX) a{' '}
                    <strong>32 dias</strong> (crédito).
                  </p>
                  <p>
                    O repasse é <strong>quinzenal</strong>: o que liquidou entre
                    os dias 01 e 15 cai no dia 20, e o que liquidou do dia 16 ao
                    fim do mês cai no dia 05 do mês seguinte.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <PlatformFeesDialog fees={fee_table} />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => router.visit('/payouts')}
                    >
                      Ver meus repasses
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Agendamento de Aulas</CardTitle>
                <CardDescription className="text-sm">
                  Configure como os alunos visualizam as aulas disponíveis no
                  aplicativo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Mostrar professor nas aulas disponíveis
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Quando ativado, o aluno vê o nome do professor ao escolher
                      uma aula. Quando desativado, apenas o horário é exibido e
                      o admin associa o professor depois.
                    </p>
                  </div>
                  <Switch
                    checked={showTeacher}
                    onCheckedChange={setShowTeacher}
                  />
                </div>

                <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground space-y-1">
                  {showTeacher ? (
                    <>
                      <p className="font-medium text-foreground">
                        Modo: Aluno escolhe o professor
                      </p>
                      <p>
                        O aluno vê o nome do professor em cada horário
                        disponível e pode escolher com quem quer fazer aula.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-foreground">
                        Modo: Admin associa o professor
                      </p>
                      <p>
                        O aluno vê apenas os horários disponíveis. O admin
                        define qual professor ficará com cada aula após a
                        reserva.
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Visualização de inscritos na aula
                </CardTitle>
                <CardDescription className="text-sm">
                  Controle quem, no aplicativo, pode ver a lista de quem está
                  inscrito em cada horário (nome e foto)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Alunos podem ver quem está inscrito
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Quando ativado, o aluno vê no app o nome e a foto dos
                      outros alunos inscritos no mesmo horário.
                    </p>
                  </div>
                  <Switch
                    checked={showParticipantsToStudents}
                    onCheckedChange={setShowParticipantsToStudents}
                  />
                </div>

                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Professores podem ver quem está inscrito
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Quando ativado, o professor vê no app a lista completa de
                      alunos inscritos nas aulas dele.
                    </p>
                  </div>
                  <Switch
                    checked={showParticipantsToTeachers}
                    onCheckedChange={setShowParticipantsToTeachers}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Fila de Espera</CardTitle>
                <CardDescription className="text-sm">
                  Controle se os alunos podem entrar na fila de espera quando um
                  horário está lotado
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Permitir fila de espera nas aulas
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Quando ativado, o aluno pode entrar na fila de espera de
                      um horário lotado e é promovido automaticamente caso surja
                      uma vaga. Quando desativado, o botão "Entrar na fila" não
                      aparece no aplicativo.
                    </p>
                  </div>
                  <Switch
                    checked={waitlistEnabled}
                    onCheckedChange={setWaitlistEnabled}
                  />
                </div>

                <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground space-y-1">
                  {waitlistEnabled ? (
                    <>
                      <p className="font-medium text-foreground">
                        Fila de espera ativada
                      </p>
                      <p>
                        Em horários lotados, o aluno pode entrar na fila e será
                        promovido automaticamente quando uma vaga abrir.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-foreground">
                        Fila de espera desativada
                      </p>
                      <p>
                        Horários lotados aparecem como indisponíveis e o aluno
                        não pode entrar em nenhuma fila de espera.
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Escolha de Prancha</CardTitle>
                <CardDescription className="text-sm">
                  Controle se o aluno precisa escolher uma prancha ao agendar a
                  aula
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Exigir escolha de prancha no agendamento
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Quando ativado, ao agendar o aluno precisa informar se vai
                      usar a própria prancha ou uma prancha da escola — e, neste
                      caso, escolher uma das pranchas disponíveis naquele
                      horário (do inventário em Equipamentos). Quando
                      desativado, o agendamento segue sem pedir prancha.
                    </p>
                  </div>
                  <Switch
                    checked={requireBoardSelection}
                    onCheckedChange={setRequireBoardSelection}
                  />
                </div>

                <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground space-y-1">
                  {requireBoardSelection ? (
                    <>
                      <p className="font-medium text-foreground">
                        Escolha de prancha ativada
                      </p>
                      <p>
                        O aluno vê a lista de pranchas livres no horário. Uma
                        prancha já reservada naquele horário não aparece para os
                        demais alunos.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-foreground">
                        Escolha de prancha desativada
                      </p>
                      <p>
                        O agendamento não pede prancha. Você pode continuar
                        associando a prancha de cada aluno na edição da aula.
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Prazos de Agendamento e Cancelamento
                </CardTitle>
                <CardDescription className="text-sm">
                  Defina com quanta antecedência o aluno pode agendar ou
                  cancelar uma aula pelo aplicativo. Escolha horas ou minutos.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
                  <div className="space-y-2">
                    <Label htmlFor="scheduling_deadline">
                      Antecedência para agendar
                    </Label>
                    <div className="flex items-end gap-2">
                      <Input
                        id="scheduling_deadline"
                        type="number"
                        min="0"
                        max={schedulingUnit === 'hours' ? 72 : 1440}
                        step="1"
                        className="w-24"
                        value={schedulingValue}
                        onChange={(e) =>
                          setSchedulingValue(parseInt(e.target.value) || 0)
                        }
                      />
                      <Select
                        value={schedulingUnit}
                        onValueChange={(v) => {
                          const next = v as DeadlineUnit
                          setSchedulingValue(
                            minutesToValue(
                              valueToMinutes(schedulingValue, schedulingUnit),
                              next,
                            ),
                          )
                          setSchedulingUnit(next)
                        }}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hours">Horas</SelectItem>
                          <SelectItem value="minutes">Minutos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      O aluno precisa agendar com no mínimo {schedulingValue}{' '}
                      {deadlineUnitLabel(schedulingUnit, schedulingValue)} de
                      antecedência
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="cancellation_deadline">
                      Antecedência para cancelar
                    </Label>
                    <div className="flex items-end gap-2">
                      <Input
                        id="cancellation_deadline"
                        type="number"
                        min="0"
                        max={cancellationUnit === 'hours' ? 72 : 1440}
                        step="1"
                        className="w-24"
                        value={cancellationValue}
                        onChange={(e) =>
                          setCancellationValue(parseInt(e.target.value) || 0)
                        }
                      />
                      <Select
                        value={cancellationUnit}
                        onValueChange={(v) => {
                          const next = v as DeadlineUnit
                          setCancellationValue(
                            minutesToValue(
                              valueToMinutes(
                                cancellationValue,
                                cancellationUnit,
                              ),
                              next,
                            ),
                          )
                          setCancellationUnit(next)
                        }}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hours">Horas</SelectItem>
                          <SelectItem value="minutes">Minutos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      O aluno precisa cancelar com no mínimo {cancellationValue}{' '}
                      {deadlineUnitLabel(cancellationUnit, cancellationValue)}{' '}
                      de antecedência
                    </p>
                  </div>
                </div>

                <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground">Exemplo:</p>
                  <p>
                    Aula às 06:00 com prazo de {cancellationValue}{' '}
                    {deadlineUnitLabel(cancellationUnit, cancellationValue)} — o
                    aluno pode cancelar até às{' '}
                    {formatDeadlineExample(
                      valueToMinutes(cancellationValue, cancellationUnit),
                    )}
                    .
                  </p>
                  <p>
                    Aula às 06:00 com prazo de {schedulingValue}{' '}
                    {deadlineUnitLabel(schedulingUnit, schedulingValue)} — o
                    aluno pode agendar até às{' '}
                    {formatDeadlineExample(
                      valueToMinutes(schedulingValue, schedulingUnit),
                    )}
                    .
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Horizonte de Agendamento
                </CardTitle>
                <CardDescription className="text-sm">
                  Defina com quanto tempo de antecedência as aulas serão geradas
                  automaticamente a partir da grade dos professores
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap items-end gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="booking_horizon">Valor</Label>
                    <Input
                      id="booking_horizon"
                      type="number"
                      min="1"
                      max={horizonLimit.max}
                      step="1"
                      className="w-32"
                      value={bookingHorizon}
                      onChange={(e) => {
                        const v = parseInt(e.target.value) || 1
                        setBookingHorizon(Math.min(v, horizonLimit.max))
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="booking_horizon_unit">Unidade</Label>
                    <Select
                      value={bookingHorizonUnit}
                      onValueChange={(v) => {
                        const next = v as HorizonUnit
                        setBookingHorizonUnit(next)
                        if (bookingHorizon > HORIZON_LIMITS[next].max) {
                          setBookingHorizon(HORIZON_LIMITS[next].max)
                        }
                      }}
                    >
                      <SelectTrigger id="booking_horizon_unit" className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="days">Dias</SelectItem>
                        <SelectItem value="weeks">Semanas</SelectItem>
                        <SelectItem value="months">Meses</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Aulas serão geradas automaticamente até {bookingHorizon}{' '}
                  {horizonLabel} à frente a partir da grade de cada professor
                </p>
              </CardContent>
            </Card>

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowTeacher(school.show_teacher_to_students)
                  setSchedulingUnit(
                    inferDeadlineUnit(school.scheduling_deadline_minutes),
                  )
                  setSchedulingValue(
                    minutesToValue(
                      school.scheduling_deadline_minutes,
                      inferDeadlineUnit(school.scheduling_deadline_minutes),
                    ),
                  )
                  setCancellationUnit(
                    inferDeadlineUnit(school.cancellation_deadline_minutes),
                  )
                  setCancellationValue(
                    minutesToValue(
                      school.cancellation_deadline_minutes,
                      inferDeadlineUnit(school.cancellation_deadline_minutes),
                    ),
                  )
                  setBookingHorizon(school.booking_horizon_months)
                  setBookingHorizonUnit(school.booking_horizon_unit)
                  setWaitlistEnabled(school.waitlist_enabled)
                  setRequireBoardSelection(school.require_board_selection)
                  setStudentBookingDisabled(school.student_booking_disabled)
                  setStudentPaymentDisabled(school.student_payment_disabled)
                  setShowParticipantsToStudents(
                    school.show_lesson_participants_to_students,
                  )
                  setShowParticipantsToTeachers(
                    school.show_lesson_participants_to_teachers,
                  )
                }}
                disabled={isAppSubmitting}
              >
                Cancelar
              </Button>
              <Button onClick={handleAppSubmit} disabled={isAppSubmitting}>
                {isAppSubmitting ? 'Salvando...' : 'Salvar Configurações'}
              </Button>
            </div>
          </TabsContent>

          {/* ── TAB NOTA FISCAL ── */}
          <TabsContent value="fiscal" className="space-y-6 mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Emissão de Nota Fiscal
                </CardTitle>
                <CardDescription className="text-sm">
                  Configure se a escola emite nota fiscal e por qual provedor. A
                  nota é emitida automaticamente ao confirmar um pagamento.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Emitir nota fiscal</p>
                    <p className="text-sm text-muted-foreground">
                      Quando ativado, a escola emite nota fiscal para os
                      pagamentos recebidos. É necessário escolher um provedor de
                      emissão.
                    </p>
                  </div>
                  <Switch
                    checked={fiscalEnabled}
                    onCheckedChange={setFiscalEnabled}
                  />
                </div>

                {fiscalEnabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl">
                    <div className="space-y-2">
                      <Label htmlFor="fiscal_document_scope">
                        Escopo da nota
                      </Label>
                      <Select
                        value={fiscalDocumentScope}
                        onValueChange={(v) =>
                          setFiscalDocumentScope(v as FiscalDocumentScope)
                        }
                      >
                        <SelectTrigger
                          id="fiscal_document_scope"
                          className="w-full"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="service_only">Serviço</SelectItem>
                          <SelectItem
                            value="service_and_product"
                            disabled={!canUseProductScope}
                          >
                            Serviço + Produto
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {!canUseProductScope && (
                        <p className="text-xs text-muted-foreground">
                          "Serviço + Produto" fica disponível após cadastrar
                          produtos na escola.
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="fiscal_provider">Provedor</Label>
                      <Select
                        value={fiscalProvider}
                        onValueChange={(v) =>
                          setFiscalProvider(v as FiscalProvider)
                        }
                      >
                        <SelectTrigger id="fiscal_provider" className="w-full">
                          <SelectValue placeholder="Selecione o provedor" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="conta_azul">Conta Azul</SelectItem>
                        </SelectContent>
                      </Select>
                      {!fiscalProvider && (
                        <p className="text-xs text-destructive">
                          Obrigatório quando a emissão está ativa.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Conexão OAuth com a Conta Azul (CA-1) */}
                {fiscalEnabled && fiscalProvider === 'conta_azul' && (
                  <div className="rounded-md border p-4 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          Conexão com a Conta Azul
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Autorize o Bee Wave a emitir notas fiscais na conta da
                          Conta Azul desta escola.
                        </p>
                      </div>
                      {conta_azul.status === 'connected' && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-700">
                          <Check className="h-3.5 w-3.5" /> Conectado
                        </span>
                      )}
                      {conta_azul.status === 'provider_unstable' && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
                          <AlertTriangle className="h-3.5 w-3.5" /> Conta Azul
                          instável
                        </span>
                      )}
                      {conta_azul.status === 'needs_reconnect' && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 px-2.5 py-1 text-xs font-medium text-destructive">
                          <AlertTriangle className="h-3.5 w-3.5" /> Autorização
                          revogada
                        </span>
                      )}
                      {conta_azul.status === 'disconnected' && (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                          Desconectado
                        </span>
                      )}
                    </div>

                    {!conta_azul.configured && (
                      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                          Credenciais da Conta Azul não configuradas no servidor
                          (variáveis{' '}
                          <code className="rounded bg-amber-100 px-1">
                            CONTA_AZUL_*
                          </code>
                          ). A conexão fica indisponível até que sejam
                          definidas.
                        </span>
                      </div>
                    )}

                    {conta_azul.status === 'connected' &&
                      conta_azul.connected_at && (
                        <p className="text-xs text-muted-foreground">
                          Conectado desde{' '}
                          {new Date(conta_azul.connected_at).toLocaleDateString(
                            'pt-BR',
                          )}
                          .
                        </p>
                      )}

                    {conta_azul.status === 'provider_unstable' && (
                      <p className="text-xs text-amber-700">
                        A Conta Azul não respondeu na última renovação. A
                        conexão continua válida e as vendas ficam guardadas —
                        não é preciso reconectar.
                      </p>
                    )}

                    {conta_azul.status === 'needs_reconnect' && (
                      <p className="text-xs text-destructive">
                        A autorização foi revogada
                        {conta_azul.revoked_at
                          ? ` em ${new Date(conta_azul.revoked_at).toLocaleDateString('pt-BR')}`
                          : ''}
                        . Reconecte para as vendas voltarem a ser enviadas — as
                        que ficaram para trás são recuperadas automaticamente.
                      </p>
                    )}

                    <div className="flex flex-wrap gap-3">
                      {conta_azul.status === 'connected' ||
                      conta_azul.status === 'provider_unstable' ? (
                        <>
                          <Button
                            variant="outline"
                            onClick={handleContaAzulConnect}
                            disabled={!conta_azul.configured}
                          >
                            Reconectar
                          </Button>
                          <Button
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={handleContaAzulDisconnect}
                            disabled={isContaAzulDisconnecting}
                          >
                            {isContaAzulDisconnecting
                              ? 'Desconectando...'
                              : 'Desconectar'}
                          </Button>
                        </>
                      ) : (
                        <Button
                          onClick={handleContaAzulConnect}
                          disabled={!conta_azul.configured}
                        >
                          {conta_azul.status === 'needs_reconnect'
                            ? 'Reconectar Conta Azul'
                            : 'Conectar Conta Azul'}
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground space-y-1">
                  {fiscalEnabled ? (
                    <>
                      <p className="font-medium text-foreground">
                        {fiscalProvider === 'conta_azul' && contaAzulHalted
                          ? 'Emissão ativada, mas sem integração'
                          : 'Emissão ativada'}
                      </p>
                      {/*
                        Só avisa quando a integração está REALMENTE parada. Antes
                        este bloco disparava para qualquer status != 'connected',
                        e como o access token vence de hora em hora, a escola via
                        "nenhuma venda está sendo enviada" com tudo funcionando.
                      */}
                      {fiscalProvider === 'conta_azul' && contaAzulHalted && (
                        <p className="text-amber-700">
                          A Conta Azul está{' '}
                          {conta_azul.status === 'needs_reconnect'
                            ? 'com a autorização revogada'
                            : 'desconectada'}
                          : as vendas ficam guardadas e não estão sendo
                          enviadas. Reconecte acima para retomar — o que ficou
                          para trás é recuperado automaticamente.
                        </p>
                      )}
                      <p>
                        A escola emite nota fiscal
                        {fiscalDocumentScope === 'service_and_product'
                          ? ' de serviço e produto'
                          : ' de serviço'}
                        {fiscalProvider ? ' via Conta Azul' : ''}.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="font-medium text-foreground">
                        Emissão desativada
                      </p>
                      <p>
                        Nenhuma nota fiscal é emitida automaticamente para os
                        pagamentos da escola.
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {school.fiscal_enabled && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Mapeamento fiscal</CardTitle>
                  <CardDescription className="text-sm">
                    Cada serviço (pacote) precisa de um código de serviço
                    municipal para emitir nota. Serviços sem mapeamento não têm
                    nota emitida.
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    onClick={() => router.get('/settings/fiscal_mappings')}
                  >
                    <FileText className="h-4 w-4 mr-1.5" />
                    Gerenciar mapeamento fiscal
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => router.get('/settings/fiscal_invoices')}
                  >
                    <FileText className="h-4 w-4 mr-1.5" />
                    Notas fiscais emitidas
                  </Button>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setFiscalEnabled(school.fiscal_enabled)
                  setFiscalDocumentScope(school.fiscal_document_scope)
                  setFiscalProvider(school.fiscal_provider ?? '')
                }}
                disabled={isFiscalSubmitting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleFiscalSubmit}
                disabled={isFiscalSubmitting}
              >
                {isFiscalSubmitting ? 'Salvando...' : 'Salvar Configurações'}
              </Button>
            </div>
          </TabsContent>

          {/* ── TAB PÁGINA PÚBLICA ── */}
          <TabsContent value="public">
            <PublicPageSettings
              school={school}
              gallery={public_gallery}
              hasTrialPackage={has_trial_package}
              subcategories={explore_subcategories}
            />
          </TabsContent>
        </Tabs>

        {/* ── ZONA DE PERIGO (apenas escola de teste) ── */}
        {can_reset_data && (
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Zona de Perigo
              </CardTitle>
              <CardDescription className="text-sm">
                Esta é a escola de teste. Use o botão abaixo para zerar todos os
                dados e começar do zero.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 rounded-md border border-destructive/30 bg-destructive/5 p-4 md:flex-row md:items-center md:justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium">Zerar dados da escola</p>
                  <p className="text-sm text-muted-foreground">
                    Remove permanentemente alunos, professores, aulas,
                    assinaturas, pagamentos, leads, eventos, equipamentos e
                    notificações. Sua conta de administrador e as configurações
                    da escola são mantidas. Esta ação não pode ser desfeita.
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      disabled={isResetting}
                      className="shrink-0"
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      {isResetting ? 'Zerando...' : 'Zerar dados'}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Zerar todos os dados da escola?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Todos os alunos, professores, aulas, assinaturas,
                        pagamentos, leads, eventos, equipamentos e notificações
                        serão removidos permanentemente. Apenas a sua conta de
                        administrador e as configurações da escola serão
                        mantidas. Esta ação não pode ser desfeita.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleResetData}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Sim, zerar dados
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  )
}
